// Which entries the map Options dialog shows, and where. Each map opts in through
// MapOptionsDialog's show* props. Laid out as the apps' map options sheet (Android
// MapOptions.section() / MapOptionsSheet.kt): a Navigation section, a Tools section, and one
// link at the foot. PRO+ tools sit in their own section with a blue icon rather than in a
// separate PRO+ group. Trello #1096.
import type { OptionsIcon } from '../components/options_menu/optionsIcons';
import type { OptionsSectionKey } from '../components/options_menu/optionsSections';

export type MapOptionEvent =
  | 'edit-details' | 'edit' | 'create' | 'waypoints' | 'goto' | 'flyover'
  | 'download' | 'embed' | 'report-issue' | 'measurement' | 'print';

export interface MapOptionsVisibility {
  showEditDetails: boolean;
  editHike: boolean;
  showCreate: boolean;
  showWaypoints: boolean;
  showGoTo: boolean;
  showFlyover: boolean;
  showDownload: boolean;
  showEmbed: boolean;
  showReportIssue: boolean;
  showMeasurement: boolean;
  showPrint: boolean;
}

export interface MapOptionItem {
  event: MapOptionEvent;
  /** i18n key for the label. */
  labelKey: string;
  /** The apps' options_ic_* glyph for this action. */
  icon: OptionsIcon;
  /** A PRO+ tool: drawn with the blue icon. */
  pro?: true;
  /** Shows a "New" tag after the label. */
  isNew?: true;
}

export interface MapOptionSection {
  key: OptionsSectionKey;
  items: MapOptionItem[];
}

type Entry = MapOptionItem & { show: keyof MapOptionsVisibility };

// Within each section, the apps' order: planning before editing, the route before the map.
const NAVIGATION: Entry[] = [
  { show: 'showCreate', event: 'create', labelKey: 'map_options_dialog.plan_a_hike', icon: 'add_circle' },
  { show: 'editHike', event: 'edit', labelKey: 'map_options_dialog.open_route_in_planner', icon: 'pin' },
  { show: 'showWaypoints', event: 'waypoints', labelKey: 'map_options_dialog.waypoints', icon: 'flag' },
  { show: 'showGoTo', event: 'goto', labelKey: 'map_options_dialog.go_to_location', icon: 'location' },
  { show: 'showMeasurement', event: 'measurement', labelKey: 'map_options_dialog.measurement_tool', icon: 'ruler', pro: true, isNew: true },
];

const TOOLS: Entry[] = [
  { show: 'showEditDetails', event: 'edit-details', labelKey: 'shared.edit_details', icon: 'pencil' },
  { show: 'showFlyover', event: 'flyover', labelKey: 'shared.3d_flyover', icon: 'mountain' },
  { show: 'showDownload', event: 'download', labelKey: 'shared.download', icon: 'download' },
  { show: 'showEmbed', event: 'embed', labelKey: 'embed_builder.modal.embed_map', icon: 'code' },
  { show: 'showPrint', event: 'print', labelKey: 'map_options_dialog.print_hike_map', icon: 'print', pro: true },
];

// A link at the foot rather than a tile, like the apps' "Contact support".
const FOOTER: Entry = { show: 'showReportIssue', event: 'report-issue', labelKey: 'map_options_dialog.report_an_issue', icon: 'warning' };

const pick = (entries: Entry[], visibility: MapOptionsVisibility): MapOptionItem[] =>
  entries.filter((entry) => visibility[entry.show]).map(({ show, ...item }) => item);

export function mapOptionsItems(visibility: MapOptionsVisibility): { sections: MapOptionSection[]; footer: MapOptionItem | null } {
  const sections: MapOptionSection[] = [
    { key: 'navigation', items: pick(NAVIGATION, visibility) },
    { key: 'tools', items: pick(TOOLS, visibility) },
  ];
  return {
    // A section with nothing in it is left out, title and all.
    sections: sections.filter((section) => section.items.length > 0),
    footer: pick([FOOTER], visibility)[0] ?? null,
  };
}
